<template>
  <div>
    <div class="grid gap-3 md:grid-cols-2">
        <label
            v-for="option in visibilityOptions"
            :key="option.value"
            :class="[
                disabled ? 'cursor-wait opacity-70' : 'cursor-pointer',
                'relative flex items-start gap-4 rounded-2xl border p-4 transition ease-in-out duration-150 focus-within:ring-2 focus-within:ring-brand',
                modelValue === option.value
                    ? 'border-brand bg-white shadow-md ring-1 ring-brand'
                    : 'border-slate-200 bg-slate-50/70 hover:border-slate-300 hover:bg-white'
            ]"
        >
            <input
                type="radio"
                :name="name"
                class="sr-only"
                :value="option.value"
                :checked="modelValue === option.value"
                :disabled="disabled"
                @change="$emit('update:modelValue', option.value)"
            >

            <span
                :class="[
                    'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition',
                    modelValue === option.value
                        ? 'bg-brand text-white'
                        : 'border border-slate-200 bg-white text-slate-400'
                ]"
            >
                <!-- Globe -->
                <svg v-if="option.value === 'public'" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />
                </svg>
                <!-- Lock -->
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                </svg>
            </span>

            <span class="flex flex-col gap-1 pr-6">
                <span class="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    {{ option.label }}
                    <span v-if="option.recommended" class="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">Recommended</span>
                </span>
                <span class="text-xs leading-relaxed text-slate-500">{{ option.description }}</span>
            </span>

            <!-- Selected tick -->
            <span
                :class="[
                    'absolute right-4 top-4 flex h-5 w-5 items-center justify-center rounded-full border transition',
                    modelValue === option.value
                        ? 'border-brand bg-brand text-white'
                        : 'border-slate-300 bg-white text-transparent'
                ]"
            >
                <svg class="h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"></path>
                </svg>
            </span>
        </label>
    </div>

    <!-- What the selected option means -->
    <div class="mt-3 rounded-2xl border border-slate-100 bg-slate-50/70 px-4 py-3">
        <p class="text-xs font-medium text-slate-700">With a {{ selectedVisibility.label.toLowerCase() }} profile:</p>
        <ul class="mt-2 space-y-1.5">
            <li v-for="point in selectedVisibility.points" :key="point.text" class="flex items-start gap-2 text-xs text-slate-600">
                <svg v-if="point.included" class="mt-px h-4 w-4 shrink-0 text-emerald-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"></path>
                </svg>
                <svg v-else class="mt-px h-4 w-4 shrink-0 text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
                <span>{{ point.text }}</span>
            </li>
        </ul>
    </div>
  </div>
</template>

<script>
export default {
    name: 'ProfileVisibilityPicker',
    props: {
        modelValue: { type: String, default: 'public' },
        name: { type: String, default: 'profileVisibility' },
        disabled: { type: Boolean, default: false }
    },
    emits: ['update:modelValue'],
    data() {
        return {
            visibilityOptions: [
                {
                    value: 'public',
                    label: 'Public',
                    recommended: true,
                    description: 'Companies can find you in search and contact you about job opportunities.',
                    points: [
                        { included: true, text: 'You appear when companies search for people with your skills and experience' },
                        { included: true, text: 'Companies can reach out to you directly about job opportunities' },
                        { included: true, text: 'You can still browse and apply for jobs yourself' }
                    ]
                },
                {
                    value: 'private',
                    label: 'Private',
                    recommended: false,
                    description: 'No one can find or view your profile. You reach out to companies on your terms.',
                    points: [
                        { included: false, text: 'You won\'t appear in any company searches' },
                        { included: false, text: 'Companies can\'t contact you about job opportunities' },
                        { included: true, text: 'You can still browse and apply for jobs. Only the companies you apply to will see your details' }
                    ]
                }
            ],
        }
    },
    computed: {
        selectedVisibility() {
            return this.visibilityOptions.find(option => option.value === this.modelValue) || this.visibilityOptions[0];
        }
    }
}
</script>
